import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent
} from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, Headphones, Search, Users, X } from 'lucide-react'
import { adminApi, type SearchResult } from '../../lib/resources'
import { ApiError } from '../../lib/api'
import styles from './GlobalSearch.module.css'

const DEBOUNCE_MS = 200
const MIN_QUERY_LEN = 2

/**
 * Búsqueda global del super admin (B6 reform consolas, 2026-05-08).
 *
 * Typeahead con debounce 200 ms — pega a `GET /admin/search?q=...` que
 * devuelve resultados unificados {kind, id, label, sub} de clientes,
 * eventos y operadores. Cap server-side ~30. Sin paginación.
 *
 * Comportamiento:
 *   - Focus en input + tipear ≥ 2 chars → fetch debounced.
 *   - Resultados en dropdown linkeable (cada uno navega a su detalle).
 *   - Esc o click fuera → cierra dropdown.
 *   - Arrow up/down + Enter → keyboard nav.
 */
export function GlobalSearch() {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Debounced fetch.
  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length < MIN_QUERY_LEN) {
      setResults([])
      setError(null)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    const handle = window.setTimeout(async () => {
      try {
        const res = await adminApi.search(trimmed)
        if (cancelled) return
        setResults(res)
        setHighlight(0)
      } catch (err) {
        if (cancelled) return
        setResults([])
        setError(err instanceof ApiError ? err.message : 'Error en la búsqueda.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [query])

  // Click fuera → cierra.
  useEffect(() => {
    if (!open) return undefined
    const onDocClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const goToResult = useCallback(
    (r: SearchResult) => {
      const path =
        r.kind === 'client'
          ? `/app/admin/clientes/${r.id}`
          : r.kind === 'event'
            ? `/app/admin/eventos/${r.id}`
            : `/app/admin/operadores/${r.id}`
      navigate(path)
      setOpen(false)
      setQuery('')
      inputRef.current?.blur()
    },
    [navigate]
  )

  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false)
      inputRef.current?.blur()
      return
    }
    if (results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => (h + 1) % results.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => (h - 1 + results.length) % results.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const r = results[highlight]
      if (r) goToResult(r)
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
    setOpen(true)
  }

  const showDropdown =
    open &&
    query.trim().length >= MIN_QUERY_LEN &&
    (loading || results.length > 0 || error !== null)

  return (
    <div className={styles.root} ref={containerRef}>
      <div className={styles.inputWrap}>
        <Search
          size={16}
          className={styles.icon}
          aria-hidden
        />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          placeholder="Buscar clientes, eventos, operadores…"
          className={styles.input}
          value={query}
          onChange={handleChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
        />
        {query && (
          <button
            type="button"
            className={styles.clearBtn}
            aria-label="Limpiar búsqueda"
            onClick={() => {
              setQuery('')
              setResults([])
              inputRef.current?.focus()
            }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className={styles.dropdown} role="listbox">
          {loading && (
            <div className={styles.statusRow}>Buscando…</div>
          )}
          {error && (
            <div className={`${styles.statusRow} ${styles.statusError}`}>
              {error}
            </div>
          )}
          {!loading &&
            !error &&
            results.length === 0 &&
            query.trim().length >= MIN_QUERY_LEN && (
              <div className={styles.statusRow}>Sin resultados.</div>
            )}
          {!loading &&
            !error &&
            results.map((r, i) => (
              <button
                key={`${r.kind}-${r.id}`}
                type="button"
                role="option"
                aria-selected={i === highlight}
                className={`${styles.result} ${i === highlight ? styles.resultActive : ''}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => goToResult(r)}
              >
                <span className={styles.resultIcon} aria-hidden>
                  {iconForKind(r.kind)}
                </span>
                <span className={styles.resultMain}>
                  <span className={styles.resultLabel}>{r.label}</span>
                  <span className={styles.resultSub}>{r.sub}</span>
                </span>
                <span className={styles.resultKind}>{kindLabel(r.kind)}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  )
}

function iconForKind(kind: SearchResult['kind']) {
  switch (kind) {
    case 'client':
      return <Users size={16} />
    case 'event':
      return <Calendar size={16} />
    case 'operator':
      return <Headphones size={16} />
  }
}

function kindLabel(kind: SearchResult['kind']): string {
  switch (kind) {
    case 'client':
      return 'Cliente'
    case 'event':
      return 'Evento'
    case 'operator':
      return 'Operador'
  }
}
